import { useState } from 'react';
import { Clock, TriangleAlert } from 'lucide-react';
import { errorMessage } from '../../../lib/api';
import { resendMagicLink } from '../api';
import { PhoneFrame } from '../frame/PhoneFrame';
import styles from '../frame/Phone.module.css';
import type { PortalMagicExpired } from '../types';
import local from './Activation.module.css';

/** Mandantenportal 10 „Dieser Link ist abgelaufen" (410): the last step, a new link, the advisor's phone. */
export function ExpiredScreen({ token, expired }: { token: string; expired: PortalMagicExpired }) {
  const [pending, setPending] = useState(false);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function resend() {
    setPending(true);
    setError(null);
    try {
      setSentTo((await resendMagicLink(token)).maskedEmail);
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setPending(false);
    }
  }

  const advisor = expired.advisor;
  return (
    <PhoneFrame title="Mandantenportal">
      <div className={local.expiredHead}>
        <span className={local.expiredIcon} aria-hidden="true">
          <Clock size={24} strokeWidth={1.5} />
        </span>
        <h1 className={local.expiredTitle}>Dieser Link ist abgelaufen</h1>
        <p className={styles.text}>
          Aus Sicherheitsgründen gilt der Zugang 14 Tage. Ihre bisherigen Eingaben sind gespeichert und gehen
          nicht verloren.
        </p>
      </div>
      {expired.lastStep ? (
        <div className={local.lastStep}>
          <div className={local.lastStepLabel}>Zuletzt bearbeitet</div>
          <div className={local.lastStepValue}>
            {expired.lastStep.label} · {expired.lastStep.on}
          </div>
        </div>
      ) : null}
      <div className={`${styles.footer} ${local.expiredFooter}`}>
        {error ? (
          <div className={styles.banner} role="alert">
            <TriangleAlert size={17} strokeWidth={1.75} aria-hidden="true" />
            <div className={styles.bannerTitle}>{error}</div>
          </div>
        ) : null}
        {sentTo ? (
          <div className={styles.notice} role="status">
            Wir haben Ihnen einen neuen Link an {sentTo} geschickt.
          </div>
        ) : (
          <button
            type="button"
            className={`pressable ${styles.primary}`}
            onClick={() => void resend()}
            disabled={pending}
          >
            Neuen Link anfordern
          </button>
        )}
        {advisor?.phone ? (
          <a href={`tel:${advisor.phone.replace(/[^\d+]/g, '')}`} className={`pressable ${styles.secondary}`}>
            {advisor.name} anrufen
          </a>
        ) : null}
      </div>
    </PhoneFrame>
  );
}

/** A magic link that does not exist (404, own text) or could not be loaded. */
export function InvalidScreen({ notFound }: { notFound: boolean }) {
  return (
    <PhoneFrame title="Mandantenportal">
      <div className={local.expiredHead}>
        <span className={local.expiredIcon} aria-hidden="true">
          <TriangleAlert size={24} strokeWidth={1.5} />
        </span>
        <h1 className={local.expiredTitle}>
          {notFound ? 'Dieser Link ist ungültig.' : 'Der Link lässt sich gerade nicht öffnen.'}
        </h1>
        <p className={styles.text}>
          {notFound
            ? 'Bitte prüfen Sie den Link aus Ihrer E-Mail. Bei Fragen hilft Ihnen Ihr Berater weiter.'
            : 'Bitte versuchen Sie es in einem Moment erneut.'}
        </p>
      </div>
      {!notFound ? (
        <div className={`${styles.footer} ${local.expiredFooter}`}>
          <button
            type="button"
            className={`pressable ${styles.secondary}`}
            onClick={() => window.location.reload()}
          >
            Erneut laden
          </button>
        </div>
      ) : null}
    </PhoneFrame>
  );
}
